import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wrapper } from "components/core/Header/styles.header";
import BackButton from "../Button/BackButton";
import { Loader } from "../Common/Loader";
import { STATUS_BEGIN } from "utils/constants/others";
import usePayoutsStore from "./usePayoutsStore";
import {
  Screen,
  TabBar,
  TabItem,
  FilterRow,
  FilterChip,
  ListHeader,
  CountText,
  Row,
  RowIcon,
  RowBody,
  RowTitle,
  RowSubtitle,
  RowRight,
  Amount,
  StatusPill,
  EmptyState,
  ScrollList,
  DateChipWrapper,
  SheetOverlay,
  Sheet,
  SheetHeader,
  SheetTitle,
  SheetCloseButton,
  OptionsCard,
  OptionRow,
  OptionHeader,
  OptionLabel,
  RadioOuter,
  RadioInner,
  CustomDateRow,
  DateField,
  DateFieldTitle,
  DateFieldInput,
  ApplyButton,
  CopyButton,
} from "./styles.payouts.js";

export const PAYOUTS_TABS = {
  PAYMENTS: "PAYMENTS",
  TRANSACTIONS: "TRANSACTIONS",
};

const PAYMENT_STATUS_FILTERS = [
  { key: "upcoming", label: "Upcoming" },
  { key: "paid", label: "Paid" },
];

const TRANSACTION_STATUS_FILTERS = [
  { key: "invoices", label: "Invoices" },
  { key: "return_notes", label: "Return notes" },
  { key: "debit_note", label: "Debit note" },
  { key: "other", label: "Other" },
];

const PAYMENT_STATUS_CODES = {
  upcoming: ["PENDING", "QUEUED_FOR_PAYOUT", "FAILED"],
  paid: ["PAID"],
};

const TRANSACTION_TYPE_CODES = {
  invoices: "GRN_COMPLETE",
  return_notes: "PRN",
  debit_note: "DN",
};

const OTHER_EXCLUDABLE_TYPES = ["GRN_COMPLETE", "PRN", "DN"];

const buildPaymentStatusParam = (selectedKeys) => {
  if (!selectedKeys.length) return undefined;
  const codes = selectedKeys.flatMap((k) => PAYMENT_STATUS_CODES[k] || []);
  return codes.length ? codes.join(",") : undefined;
};

const buildTransactionParams = (selectedKeys) => {
  if (!selectedKeys.length) return {};
  const hasOther = selectedKeys.includes("other");
  const specificCodes = selectedKeys
    .filter((k) => k !== "other")
    .map((k) => TRANSACTION_TYPE_CODES[k])
    .filter(Boolean);

  if (hasOther) {
    const excluded = OTHER_EXCLUDABLE_TYPES.filter(
      (code) => !specificCodes.includes(code)
    );
    return excluded.length
      ? { excluded_transaction_types: excluded.join(",") }
      : {};
  }
  return specificCodes.length
    ? { transaction_types: specificCodes.join(",") }
    : {};
};

const DEFAULT_PAGE_SIZE = 50;

const FROM_ENTITY = { id: "HYPERPURE", type: "BUSINESS" };
const TO_ENTITY_TYPE = "VENDOR";

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const formatAmount = (value) => {
  const n = Number(value || 0);
  const sign = n < 0 ? "-" : "+";
  return `${sign} ₹${Math.abs(n).toLocaleString("en-IN")}`;
};

const toRFC3339Start = (yyyyMmDd) =>
  yyyyMmDd ? `${yyyyMmDd}T00:00:00Z` : undefined;
const toRFC3339End = (yyyyMmDd) =>
  yyyyMmDd ? `${yyyyMmDd}T23:59:59Z` : undefined;

const toYyyyMmDd = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getDefaultDueRange = () => {
  const now = new Date();
  const from = new Date(now);
  from.setDate(now.getDate() - 15);
  const to = new Date(now);
  to.setDate(now.getDate() + 5);
  return { from: toYyyyMmDd(from), to: toYyyyMmDd(to) };
};

const formatChipDate = (yyyyMmDd) => {
  if (!yyyyMmDd) return "";
  const [y, m, d] = yyyyMmDd.split("-");
  const dt = new Date(Number(y), Number(m) - 1, Number(d));
  return dt.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
};

const DURATION_PRESETS = [
  { key: "last_7", label: "Last 7 days", days: 7 },
  { key: "last_30", label: "Last 30 days", days: 30 },
  { key: "last_6m", label: "Last 6 months", months: 6 },
  { key: "custom", label: "Chose date" },
];

const computePresetRange = (preset) => {
  const to = new Date();
  const from = new Date();
  if (preset.days) {
    from.setDate(to.getDate() - preset.days);
  } else if (preset.months) {
    from.setMonth(to.getMonth() - preset.months);
  }
  return { from: toYyyyMmDd(from), to: toYyyyMmDd(to) };
};

const DateRangeChip = ({ from, to, onApply }) => {
  const [open, setOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState("custom");
  const [draftFrom, setDraftFrom] = useState(from || "");
  const [draftTo, setDraftTo] = useState(to || "");

  const label =
    from && to
      ? `Due: ${formatChipDate(from)} to ${formatChipDate(to)}`
      : "Due date";

  const openSheet = () => {
    setDraftFrom(from || "");
    setDraftTo(to || "");
    setSelectedPreset("custom");
    setOpen(true);
  };

  const closeSheet = () => setOpen(false);

  const handleSelectPreset = (preset) => {
    setSelectedPreset(preset.key);
    if (preset.key !== "custom") {
      const range = computePresetRange(preset);
      setDraftFrom(range.from);
      setDraftTo(range.to);
    } else {
      setDraftFrom((prev) => prev || from || "");
      setDraftTo((prev) => prev || to || "");
    }
  };

  const canApply = draftFrom && draftTo && draftFrom <= draftTo;

  const handleApply = () => {
    if (!canApply) return;
    onApply({ from: draftFrom, to: draftTo });
    setOpen(false);
  };

  const stopPropagation = (e) => e.stopPropagation();

  return (
    <DateChipWrapper>
      <FilterChip active={Boolean(from && to)} onClick={openSheet}>
        {label} ▾
      </FilterChip>
      {open ? (
        <SheetOverlay onClick={closeSheet}>
          <Sheet onClick={stopPropagation}>
            <SheetHeader>
              <SheetTitle>Select duration</SheetTitle>
              <SheetCloseButton onClick={closeSheet} aria-label="Close">
                ✕
              </SheetCloseButton>
            </SheetHeader>
            <OptionsCard>
              {DURATION_PRESETS.map((preset) => {
                const active = selectedPreset === preset.key;
                return (
                  <OptionRow
                    key={preset.key}
                    onClick={() => handleSelectPreset(preset)}
                  >
                    <OptionHeader>
                      <OptionLabel>{preset.label}</OptionLabel>
                      <RadioOuter active={active}>
                        <RadioInner active={active} />
                      </RadioOuter>
                    </OptionHeader>
                    {preset.key === "custom" && active ? (
                      <CustomDateRow onClick={stopPropagation}>
                        <DateField>
                          <DateFieldTitle>From</DateFieldTitle>
                          <DateFieldInput
                            type="date"
                            value={draftFrom}
                            onChange={(e) => setDraftFrom(e.target.value)}
                          />
                        </DateField>
                        <DateField>
                          <DateFieldTitle>To</DateFieldTitle>
                          <DateFieldInput
                            type="date"
                            value={draftTo}
                            onChange={(e) => setDraftTo(e.target.value)}
                          />
                        </DateField>
                      </CustomDateRow>
                    ) : null}
                  </OptionRow>
                );
              })}
            </OptionsCard>
            <ApplyButton disabled={!canApply} onClick={handleApply}>
              Apply
            </ApplyButton>
          </Sheet>
        </SheetOverlay>
      ) : null}
    </DateChipWrapper>
  );
};

// Tag + subtitle rules for a payment row:
//   accrual_status = "PAID"
//     amount < 0  -> tag: ADJUSTED (green),  subtitle: "UTR: <utr>" (copyable)
//     amount >= 0 -> tag: PAID     (green),  subtitle: "UTR: <utr>" (copyable)
//   accrual_status in ("", "PENDING", "QUEUED_FOR_PAYOUT", "FAILED")
//     amount < 0  -> tag: CARRY FORWARD (orange), subtitle: "Will be adjusted in next payout"
//     amount >= 0 -> tag: UPCOMING       (indigo), subtitle: "" (date centers vertically)
const getPaymentTagAndSubtitle = (item) => {
  const amount = Number(item.payout_amount || 0);
  const isNegative = amount < 0;
  const accrualStatus = (item.accrual_status || "").toUpperCase();
  const utr = item.payout_utr_number;

  if (accrualStatus === "PAID") {
    const displayUtr = utr && utr.length > 20 ? `${utr.slice(0, 18)}..` : utr;
    return {
      tag: isNegative ? "Adjusted" : "Paid",
      subtitle: utr ? `UTR: ${displayUtr}` : "",
      showCopy: Boolean(utr),
    };
  }
  return {
    tag: isNegative ? "Carry forward" : "Upcoming",
    subtitle: isNegative ? "Will be adjusted in next payout" : "",
    showCopy: false,
  };
};

const copyToClipboard = (text) => {
  if (!text) return;
  if (navigator?.clipboard?.writeText) {
    navigator.clipboard.writeText(text);
  }
};

const PaymentRow = ({ item }) => {
  const isDebit = Number(item.payout_amount) < 0;
  const { tag, subtitle, showCopy } = getPaymentTagAndSubtitle(item);
  return (
    <Row>
      <RowIcon>
        <img src="/logos/rupee_icon.png" alt="" />
      </RowIcon>
      <RowBody>
        <RowTitle>{formatDate(item.accrual_date)}</RowTitle>
        {subtitle ? (
          <RowSubtitle>
            {subtitle}
            {showCopy ? (
              <CopyButton
                type="button"
                onClick={() => copyToClipboard(item.payout_utr_number)}
                aria-label="Copy UTR"
              >
                ⎘
              </CopyButton>
            ) : null}
          </RowSubtitle>
        ) : null}
      </RowBody>
      <RowRight>
        <Amount negative={isDebit}>{formatAmount(item.payout_amount)}</Amount>
        {tag ? <StatusPill status={tag}>{tag}</StatusPill> : null}
      </RowRight>
    </Row>
  );
};

// Tag + amount rules for a transaction row:
//   payment_status = "SUCCESS"
//     default_accrual_direction = "DEDUCTION" -> tag: PAID (adjusted) (green)
//     default_accrual_direction = "ADDITION"  -> tag: PAID            (green)
//   otherwise
//     -> tag: Upcoming (indigo)
//
//   Amount sign:
//     default_accrual_direction = "DEDUCTION" -> negative
//     default_accrual_direction = "ADDITION"  -> positive
//
//   Amount suffix:
//     transaction_type = "GRN_COMPLETE" AND transaction_status != "SUCCESS"
//       -> append "(estd.)" in gray (estimated GRN amount, not yet finalized)
const TransactionRow = ({ item }) => {
  const isDeduction =
    (item.default_accrual_direction || "").toUpperCase() === "DEDUCTION";
  const rawAmount = Math.abs(Number(item.transaction_amount || 0));
  const signedAmount = isDeduction ? -rawAmount : rawAmount;
  const paymentStatus = (item.payment_status || "").toUpperCase();
  const transactionStatus = (item.transaction_status || "").toUpperCase();
  const transactionType = (item.transaction_type || "").toUpperCase();
  const tag =
    paymentStatus === "SUCCESS"
      ? isDeduction
        ? "Adjusted"
        : "Paid"
      : "Upcoming";
  const showEstimated =
    transactionType === "GRN_COMPLETE" && transactionStatus !== "SUCCESS";
  const dueLabel = item.consideration_date
    ? `Due ${formatDate(item.consideration_date)}`
    : item.actual_payment_date
    ? `Paid on ${formatDate(item.actual_payment_date)}`
    : "";
  const label =
    item.display_transaction_type ||
    item.parent_transaction_type ||
    item.transaction_type ||
    "Invoice";
  return (
    <Row>
      <RowIcon>
        <img src="/logos/transaction_document.png" alt="" />
      </RowIcon>
      <RowBody>
        <RowTitle>{label}</RowTitle>
        <RowSubtitle>{item.transaction_number}</RowSubtitle>
        {dueLabel ? <RowSubtitle>{dueLabel}</RowSubtitle> : null}
      </RowBody>
      <RowRight>
        <Amount negative={isDeduction}>
          {formatAmount(signedAmount)}
          {showEstimated ? (
            <span style={{ color: "#9197a6", marginLeft: 4, fontWeight: 400 }}>
              (est.)
            </span>
          ) : null}
        </Amount>
        {tag ? <StatusPill status={tag}>{tag}</StatusPill> : null}
      </RowRight>
    </Row>
  );
};

const Payouts = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(PAYOUTS_TABS.PAYMENTS);
  const [paymentsFilter, setPaymentsFilter] = useState([]);
  const [transactionsFilter, setTransactionsFilter] = useState([]);
  const [dueRange, setDueRange] = useState(getDefaultDueRange);

  const toEntityId =
    (typeof window !== "undefined" &&
      window.localStorage.getItem("outlet_id")) ||
    "";

  const handleBackClick = () => navigate("/");

  const {
    accruals,
    accrualsStatus,
    accrualsHasMore,
    transactions,
    transactionsStatus,
    transactionsHasMore,
    getVendorAccruals,
    getVendorTransactions,
    resetAccruals,
    resetTransactions,
  } = usePayoutsStore();

  const scrollRef = useRef(null);

  const switchTab = (nextTab) => {
    if (nextTab === activeTab) return;
    if (nextTab === PAYOUTS_TABS.PAYMENTS) {
      resetTransactions();
    } else {
      resetAccruals();
    }
    setActiveTab(nextTab);
  };

  const fetchAccruals = useCallback(
    (offset, append) => {
      getVendorAccruals(
        {
          page_start_offset: offset,
          page_size: DEFAULT_PAGE_SIZE,
          fetch_details: false,
          from_entity_id: FROM_ENTITY.id,
          from_entity_type: FROM_ENTITY.type,
          to_entity_id: toEntityId,
          to_entity_type: TO_ENTITY_TYPE,
          accrual_start_date: toRFC3339Start(dueRange.from),
          accrual_end_date: toRFC3339End(dueRange.to),
          accrual_statuses: buildPaymentStatusParam(paymentsFilter),
        },
        { append }
      );
    },
    [toEntityId, dueRange, paymentsFilter, getVendorAccruals]
  );

  const fetchTransactions = useCallback(
    (offset, append) => {
      getVendorTransactions(
        {
          page_start_offset: offset,
          page_size: DEFAULT_PAGE_SIZE,
          entity_id: toEntityId,
          entity_type: TO_ENTITY_TYPE,
          consideration_start_date: dueRange.from
            ? dueRange.from.replace(/-/g, "")
            : undefined,
          consideration_end_date: dueRange.to
            ? dueRange.to.replace(/-/g, "")
            : undefined,
          ...buildTransactionParams(transactionsFilter),
        },
        { append }
      );
    },
    [toEntityId, dueRange, transactionsFilter, getVendorTransactions]
  );

  useEffect(() => {
    if (activeTab === PAYOUTS_TABS.PAYMENTS) {
      fetchAccruals(0, false);
    } else {
      fetchTransactions(0, false);
    }
  }, [activeTab, fetchAccruals, fetchTransactions]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (!nearBottom) return;

    if (activeTab === PAYOUTS_TABS.PAYMENTS) {
      if (accrualsStatus === STATUS_BEGIN || !accrualsHasMore) return;
      fetchAccruals(accruals.length, true);
    } else {
      if (transactionsStatus === STATUS_BEGIN || !transactionsHasMore) return;
      fetchTransactions(transactions.length, true);
    }
  }, [
    activeTab,
    accruals.length,
    accrualsStatus,
    accrualsHasMore,
    transactions.length,
    transactionsStatus,
    transactionsHasMore,
    fetchAccruals,
    fetchTransactions,
  ]);

  const isPayments = activeTab === PAYOUTS_TABS.PAYMENTS;
  const loading = isPayments
    ? accrualsStatus === STATUS_BEGIN
    : transactionsStatus === STATUS_BEGIN;
  const items = isPayments ? accruals : transactions;
  const statusFilters = isPayments
    ? PAYMENT_STATUS_FILTERS
    : TRANSACTION_STATUS_FILTERS;
  const activeFilter = isPayments ? paymentsFilter : transactionsFilter;
  const setFilter = isPayments ? setPaymentsFilter : setTransactionsFilter;

  const toggleFilter = (key) => {
    setFilter((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const removeFilter = (key, e) => {
    e.stopPropagation();
    setFilter((prev) => prev.filter((k) => k !== key));
  };

  const countLabel = useMemo(() => {
    const n = items?.length || 0;
    return isPayments ? `${n} payments` : `${n} transactions`;
  }, [items, isPayments]);

  return (
    <React.Fragment>
      <Wrapper
        className="po-header"
        style={{ background: "#F2F4F7", height: "auto", padding: "12px 0" }}
      >
        <div className="d-flex ml-4 flex-row align-items-center">
          <span onClick={handleBackClick}>
            <BackButton />
          </span>
          <span className="fs-18 fw-700 ml-2">Payments</span>
        </div>
      </Wrapper>
      <Screen>
        <TabBar>
          <TabItem
            active={isPayments}
            onClick={() => switchTab(PAYOUTS_TABS.PAYMENTS)}
          >
            Payments
          </TabItem>
          <TabItem
            active={!isPayments}
            onClick={() => switchTab(PAYOUTS_TABS.TRANSACTIONS)}
          >
            Transactions
          </TabItem>
        </TabBar>

        <FilterRow>
          <DateRangeChip
            from={dueRange.from}
            to={dueRange.to}
            onApply={setDueRange}
          />
          {statusFilters.map((f) => {
            const selected = activeFilter.includes(f.key);
            return (
              <FilterChip
                key={f.key}
                active={selected}
                onClick={() => !selected && toggleFilter(f.key)}
              >
                {f.label}
                {selected ? (
                  <span
                    role="button"
                    aria-label={`Remove ${f.label} filter`}
                    onClick={(e) => removeFilter(f.key, e)}
                    style={{ marginLeft: 2, cursor: "pointer", fontSize: 10 }}
                  >
                    ✕
                  </span>
                ) : null}
              </FilterChip>
            );
          })}
        </FilterRow>

        <ListHeader>
          <CountText>{countLabel}</CountText>
        </ListHeader>

        <ScrollList ref={scrollRef} onScroll={handleScroll}>
          {loading && !items?.length ? (
            <Loader />
          ) : items?.length ? (
            items.map((item, idx) =>
              isPayments ? (
                <PaymentRow
                  key={item.reference_number || idx}
                  item={item}
                />
              ) : (
                <TransactionRow
                  key={item.transaction_number || idx}
                  item={item}
                />
              )
            )
          ) : (
            <EmptyState>
              No {isPayments ? "payments" : "transactions"} to show
            </EmptyState>
          )}
          {loading && items?.length ? <Loader /> : null}
        </ScrollList>
      </Screen>
    </React.Fragment>
  );
};

export default Payouts;
